import { useStore } from '@nanostores/react';
import { AnimatePresence, m, useReducedMotion } from 'motion/react';
import { atom } from 'nanostores';
import { useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react';
import { createPortal } from 'react-dom';
import { PlainButton } from '@customerio/pluma-components/react';
import { useLocalStorage } from '../../hooks/browser/use-local-storage';
import { SIDEBAR_PORTAL_FADE_ANIMATE, sidebarPortalFadeInitial, sidebarPortalFadeTransition } from '@/utils/motion';
import clsx from 'clsx';
import * as styles from './index.css';

const DEFAULT_SIDEBAR_WIDTH = 370;
const MIN_SIDEBAR_WIDTH = 370;
const MAX_SIDEBAR_WIDTH = 740;
const SIDEBAR_ANIMATION_DURATION = 150;
/** Brief delay so section exit starts, then width close overlaps for a quick close without blank full-width pause. */
const SECTION_EXIT_DURATION_MS = 100;
const targetId = 'sidebar-portal';
const activeId = atom<string | null>(null);
const closingId = atom<string | null>(null);
const sidebarWidth$ = atom(DEFAULT_SIDEBAR_WIDTH);
const companionIds$ = atom(new Map<string, number>());

export function open(id: string) {
  activeId.set(id);
  closingId.set(null);
}

export async function close() {
  closingId.set(activeId.get());
  // Wait for section exit animations (e.g. help sidebar stagger) so they are visible before the panel closes.
  await new Promise((resolve) => setTimeout(resolve, SECTION_EXIT_DURATION_MS));
  activeId.set(null);
  await new Promise((resolve) => setTimeout(resolve, SIDEBAR_ANIMATION_DURATION));
  closingId.set(null);
}

export function getActiveSidebarId(): string | null {
  return activeId.get();
}

export function toggle(id: string) {
  if (activeId.get() === id) {
    close();
  } else {
    open(id);
  }
}

// expose the sidebar functions to the window object
// so that the ember ui can hook into it and open/close the sidebar
// to prevent having two sidebars open at the same time
Object.assign(window, {
  __hydra_sidebar__: {
    open,
    close,
    toggle,
    subscribe: activeId.subscribe,
    openSupportForm: () => {
      // No-op placeholder. Replaced at runtime by HelpSidebar once it mounts.
    },
  } satisfies Window['__hydra_sidebar__'],
});

/**
 * Renders the sidebar root element.
 * This is the element that will be used as the target for the sidebar portal.
 */
const COMPANION_BOX_SHADOW = '-4px 2px 2px 0px #34344603, -12px 4px 24px 0px #34344629';

export function useSidebarCompanion(sidebarId: string) {
  const offset = useSidebarOffset(sidebarId);
  const isOpen = offset > 0;

  useEffect(() => {
    const map = new Map(companionIds$.get());
    map.set(sidebarId, (map.get(sidebarId) ?? 0) + 1);
    companionIds$.set(map);
    return () => {
      const map = new Map(companionIds$.get());
      const count = (map.get(sidebarId) ?? 1) - 1;
      if (count <= 0) {
        map.delete(sidebarId);
      } else {
        map.set(sidebarId, count);
      }
      companionIds$.set(map);
    };
  }, [sidebarId]);

  return {
    style: {
      maxWidth: isOpen ? `calc(90vw - ${offset}px)` : undefined,
      right: isOpen ? offset : undefined,
      transition: `right ${SIDEBAR_ANIMATION_DURATION}ms ease, max-width ${SIDEBAR_ANIMATION_DURATION}ms ease`,
      boxShadow: isOpen ? COMPANION_BOX_SHADOW : undefined,
      height: isOpen ? '100vh' : undefined,
      top: isOpen ? 0 : undefined,
    },
    shouldCloseOnOverlayClick: !isOpen,
    withOverlay: !isOpen,
  };
}

export function SidebarRoot() {
  const _activeSidebarId = useStore(activeId);
  const isOpen = _activeSidebarId !== null;
  const companionIds = useStore(companionIds$);
  const companion = _activeSidebarId !== null && companionIds.has(_activeSidebarId);
  const shouldReduceMotion = useReducedMotion();
  const [display, setDisplay] = useState<'block' | 'none'>('none');
  const [sidebarWidth, setSidebarWidth] = useLocalStorage('sidebar-width', DEFAULT_SIDEBAR_WIDTH);
  useEffect(() => {
    sidebarWidth$.set(sidebarWidth);
  }, [sidebarWidth]);
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!isResizing || sidebarRef.current == null) return;

      const rect = sidebarRef.current.getBoundingClientRect();
      const newWidth = rect.right - event.clientX;
      const clampedWidth = Math.min(Math.max(newWidth, MIN_SIDEBAR_WIDTH), MAX_SIDEBAR_WIDTH);
      setSidebarWidth(clampedWidth);
      sidebarWidth$.set(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, setSidebarWidth]);

  const handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
    setIsResizing(true);
  };

  return (
    <>
      {isOpen ? (
        <PlainButton
          className={styles.resizeHandleLayoutRefresh}
          onMouseDown={handleMouseDown}
          aria-label="Resize sidebar"
        />
      ) : null}
      <AnimatePresence>
        <m.div
          ref={sidebarRef}
          animate={{
            width: isOpen ? sidebarWidth : 0,
            display,
          }}
          role="complementary"
          initial={{ width: 0 }}
          // putting 0 causes a slight scroll on the html element
          // when the sidebar is closed, so we use display: none instead
          // and set the width to 0
          onAnimationStart={() => {
            if (isOpen) {
              setDisplay('block');
            }
          }}
          onAnimationComplete={() => {
            if (!isOpen) {
              setDisplay('none');
            }
          }}
          exit={{
            display: 'none',
          }}
          transition={{
            duration: isResizing || shouldReduceMotion === true ? 0 : SIDEBAR_ANIMATION_DURATION / 1000,
            ease: 'easeInOut',
          }}
          style={{
            zIndex: 40, // pluma navbar z-index
            position: 'relative',
          }}
          className={clsx(
            styles.sidebarRoot,
            styles.sidebarRootLayoutRefresh,
            companion && styles.sidebarRootCompanion,
          )}
          id={targetId}
        />
      </AnimatePresence>
    </>
  );
}

type SidebarProps = {
  id: string;
  children: React.ReactNode;
  onToggle?: (isOpen: boolean) => void;
  onToggleComplete?: (isOpen: boolean) => void;
  ref?: Ref<HTMLDivElement>;
};

export function Sidebar({ id, children, onToggle, onToggleComplete, ref }: SidebarProps) {
  const _activeSidebarId = useStore(activeId);
  const _closingSidebarId = useStore(closingId);
  const isClosing = _closingSidebarId === id;
  const shouldReduceMotion = useReducedMotion();
  const sidebarTarget = document.getElementById(targetId);
  const lastActiveId = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => containerRef.current as HTMLDivElement);

  useEffect(() => {
    const wasActive = lastActiveId.current === id;
    const isActive = _activeSidebarId === id;

    if (lastActiveId.current !== _activeSidebarId) {
      lastActiveId.current = _activeSidebarId;

      if (wasActive && !isActive) {
        onToggle?.(false);
      }

      if (!wasActive && isActive) {
        onToggle?.(true);
      }
    }
  }, [_activeSidebarId, id, onToggle]);

  if (_activeSidebarId !== id && _closingSidebarId !== id) {
    return null;
  }

  if (sidebarTarget == null) {
    return null;
  }

  // Fade only on the portal root so nested sidebars can own vertical stagger without compounding motion.
  const transition = sidebarPortalFadeTransition(isClosing, shouldReduceMotion);
  const sidebarContentInitial = sidebarPortalFadeInitial(shouldReduceMotion);

  return createPortal(
    <m.div
      ref={containerRef}
      className={styles.sidebar}
      initial={sidebarContentInitial}
      animate={SIDEBAR_PORTAL_FADE_ANIMATE}
      transition={transition}
      onAnimationComplete={() => {
        onToggleComplete?.(!isClosing);
      }}
    >
      {children}
    </m.div>,
    sidebarTarget,
  );
}

export function useSidebarActive(id: string) {
  const active = useStore(activeId) === id;
  const closing = useStore(closingId) === id;

  return active || closing;
}

/**
 * Returns whether the sidebar with the given id is currently in its closing state
 * (after close() was called but before the close animation completes).
 */
export function useSidebarClosing(id: string) {
  return useStore(closingId) === id;
}

export function useSidebarOffset(id: string) {
  const isActive = useStore(activeId) === id;
  const width = useStore(sidebarWidth$);
  return isActive ? width : 0;
}

export { SidebarHeader } from './sidebar-header';
